import React, { FC, useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { makeStyles } from '@mui/styles'
import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material'

import { Colors } from '../../../theme'
import { SelectArrow } from '../SelectArrow'
import capitalizeFirstLetter from '../../../utils/capitalizeFirstLetter'

const useStyles = makeStyles(() => ({
  inputContainer: {
    position: 'inherit',
    width: ({ width }: InputSelectProps) => width,
    marginTop: ({ mt }: InputSelectProps) => mt,
    marginBottom: ({ mb }: InputSelectProps) => mb,
    marginLeft: ({ ml }: InputSelectProps) => ml,
    marginRight: ({ mr }: InputSelectProps) => mr,
    paddingLeft: ({ pl }: InputSelectProps) => pl,
    '& .MuiSelect-select': {
      color: `rgba(255,255,255,0.6)`,
      fontSize: 14,
      padding: ({ padding }: InputSelectProps) => padding,
    },
    '& .Mui-disabled': {
      '& .MuiSelect-select': {
        color: Colors.white,
        '-webkit-text-fill-color': Colors.white,
        opacity: 0.6,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.4)',
      },
    },
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: ({ width }: InputSelectProps) => width,
    height: '40px',
    color: '#FFFFFF',
    boxSizing: 'border-box',
  },
  selectField: {
    color: '#FFFFFF',
  },
  error: {
    position: 'absolute',
  },
  disabled: {
    opacity: 0.6,
  },
}))

export interface IEnums {
  value: string
  label: string
}

interface InputSelectProps {
  enums?: IEnums[]
  labelName?: string
  inputName: string
  placeholder?: string
  width: number | string
  mt?: number | string
  mb?: number | string
  ml?: number | string
  mr?: number | string
  pl?: number | string
  padding?: number | string
  disable?: boolean | undefined
  enumDisableValue?: string
  enumDisableValues?: string[]
  defaultValue?: string
  onBlurSelect?: ($event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  errorText?: string
  placeholderDisabled?: boolean
  onChange?: (value: string) => void
}

const selectArr = [
  { id: 1, city: 'Num 1' },
  { id: 2, city: 'Num 2' },
  { id: 3, city: 'Num 3' },
]

export const InputSelect: FC<InputSelectProps> = ({
  enums,
  labelName,
  inputName,
  placeholder,
  width,
  pl,
  mb,
  ml,
  mr,
  mt,
  padding,
  disable,
  enumDisableValue,
  enumDisableValues = [],
  defaultValue = '',
  onBlurSelect,
  errorText,
  placeholderDisabled = true,
}) => {
  const styles = useStyles({ width, mt, mb, pl, placeholder, inputName, ml, mr, padding, disable })
  const {
    control,
    register,
    formState: { errors },
    setValue,
  } = useFormContext()
  const error = errors[inputName]

  const errorMessage = useMemo(() => {
    // const message = error?.message.replace(inputName, '')
    const message = error?.message
    return capitalizeFirstLetter(message)
  }, [error?.message /* , inputName */])

  useEffect(() => {
    setValue(inputName, defaultValue || '')
  }, [defaultValue, inputName, setValue])

  return (
    <Grid container item className={styles.inputContainer} direction={'column'} wrap={'nowrap'}>
      {labelName && (
        <Grid item>
          <InputLabel shrink htmlFor={inputName} className={styles.label}>
            {labelName}
          </InputLabel>
        </Grid>
      )}
      <Grid item>
        <FormControl className={styles.formControl} variant={'outlined'} size={'small'}>
          <Controller
            rules={{ required: true }}
            control={control}
            name={inputName}
            defaultValue={defaultValue || ''}
            render={({ field: { onBlur, onChange, value, ref } }) => (
              <Select
                id={inputName}
                displayEmpty
                IconComponent={SelectArrow}
                disabled={disable}
                {...register(inputName)}
                error={!!error}
                className={styles.selectField}
                inputProps={{ 'aria-label': 'Without label' }}
                value={disable ? defaultValue || '' : value && value}
                onChange={onChange}
                inputRef={ref}
                onBlur={($event) => {
                  onBlur()
                  if (onBlurSelect) {
                    onBlurSelect($event)
                  }
                }}
              >
                {placeholder && (
                  <MenuItem className={styles.disabled} value="" disabled={placeholderDisabled} key={'empty'}>
                    {placeholder}
                  </MenuItem>
                )}

                {enums &&
                  Object.keys(enums).length !== 0 &&
                  enums.map((data: IEnums) => (
                    <MenuItem
                      key={data.value}
                      disabled={data.value === enumDisableValue || enumDisableValues.includes(data.value)}
                      value={data.value}
                    >
                      {data.label}
                    </MenuItem>
                  ))}

                {!enums &&
                  selectArr.map((el) => (
                    <MenuItem key={el.id} value={el.city}>
                      {el.city}
                    </MenuItem>
                  ))}
              </Select>
            )}
          />
        </FormControl>
      </Grid>

      <Grid item>
        {errorText ? (
          <FormHelperText error id="component-error-text" className={styles.error}>
            {errorText}
          </FormHelperText>
        ) : (
          error && (
            <FormHelperText error id="component-error-text" className={styles.error}>
              {errorMessage}
            </FormHelperText>
          )
        )}
      </Grid>
    </Grid>
  )
}
